import { groupBy } from '#pplx/frontend/components/utils/groupBy.js';
import { Source } from '#pplx/frontend/components/utils/SourcesConfig.js';
export const answerContainsEntityGroupBlock = (block) => {
    return block?.entity_group_type !== 'CALENDAR_ACTION' && !!block?.entities?.length;
};
export const answerContainsInteractiveInlineCard = (inlineBlock) => {
    const hasMcq = Boolean(inlineBlock?.multiple_choice_quiz_card_block?.questions?.length);
    const hasFlashCard = Boolean(inlineBlock?.flash_card_section_block);
    return hasMcq || hasFlashCard;
};
/**
 * Checks if message has plain text chunks (legacy format, streaming)
 */
export const hasPlainChunks = (parsedMessage) => {
    return Boolean(parsedMessage.chunks && parsedMessage.chunks.length > 0);
};
/**
 * Checks if message has plain answer text (legacy format, completed)
 */
export const hasPlainAnswer = (parsedMessage) => {
    return Boolean(parsedMessage.answer && parsedMessage.answer.length > 0);
};
/**
 * Checks if message has structured chunks in markdown or entity list blocks (streaming)
 */
export const hasStructuredChunks = (parsedMessage) => {
    return !!parsedMessage.structured_answer_blocks?.some((block) => block.markdown_block?.chunks?.length || block.entity_list_block?.chunks?.length);
};
/**
 * Checks if message has completed structured answer content
 */
export const hasStructuredAnswer = (parsedMessage) => {
    return !!parsedMessage.structured_answer_blocks?.some((block) => block.markdown_block?.answer?.length ||
        block.entity_list_block?.text?.length ||
        answerContainsEntityGroupBlock(block.entity_group_block));
};
/**
 * Checks if message has interactive cards (MCQ or flashcards)
 */
export const hasInteractiveCards = (parsedMessage) => {
    return !!parsedMessage.structured_answer_blocks?.some((block) => answerContainsInteractiveInlineCard(block.inline_entity_block));
};
/**
 * Checks if message has completed generated media items (AI-generated images/videos)
 */
export const hasCompletedMedia = (parsedMessage) => {
    return !!parsedMessage.structured_answer_blocks?.some((block) => block.inline_entity_block?.media_block?.generated_media_items?.some((generatedMediaItem) => generatedMediaItem.status === 'COMPLETED'));
};
/**
 * Checks if message has media search results (images/videos from web search)
 */
export const hasMediaSearchResults = (parsedMessage) => {
    return !!parsedMessage.structured_answer_blocks?.some((block) => block.inline_entity_block?.media_block?.media_items?.some((mediaItem) => mediaItem.image || mediaItem.medium === 'image' || mediaItem.medium === 'video'));
};
/**
 * Static helper class for handling message-related operations and type checking.
 * Supports both legacy message formats and new PerplexityMessage types during migration.
 */
export class MessageHelper {
    /**
     * Whether or not the parsed message has any content (streaming or completed).
     * This is useful for determining processing states, where we want to know if
     * content exists regardless of overall completion status.
     *
     * Unlike hasAnswer(), this does not require the isStatusCompleted flag for media -
     * it checks for both completed generated media and media search results, making it
     * suitable for showing processing indicators as soon as content is available.
     */
    static hasContent(parsedMessage) {
        return (hasPlainChunks(parsedMessage) ||
            hasPlainAnswer(parsedMessage) ||
            hasStructuredChunks(parsedMessage) ||
            hasStructuredAnswer(parsedMessage) ||
            hasCompletedMedia(parsedMessage) ||
            hasMediaSearchResults(parsedMessage) ||
            hasInteractiveCards(parsedMessage));
    }
    /**
     * Whether or not the parsed message text has an answer to display as a response.
     * This checks for completed, displayable content.
     *
     * Unlike hasContent(), this requires media to be fully completed with both
     * individual item status AND the overall isStatusCompleted flag, ensuring
     * the entire message is ready for display.
     */
    static hasAnswer(parsedMessage, isStatusCompleted) {
        const hasMediaAnswer = hasCompletedMedia(parsedMessage) && isStatusCompleted;
        return (hasPlainChunks(parsedMessage) ||
            hasPlainAnswer(parsedMessage) ||
            hasStructuredChunks(parsedMessage) ||
            hasStructuredAnswer(parsedMessage) ||
            hasMediaAnswer ||
            hasInteractiveCards(parsedMessage));
    }
    /**
     * Type guard to check if a message is a PerplexityMessage (new format)
     */
    static isPerplexityMessage(message) {
        return message?.blocks !== undefined;
    }
    /**
     * Type guard to check if a message is an article section
     */
    static isPerplexityArticleSection(message) {
        return message?.message !== undefined;
    }
    /**
     * Type guard to check if a message is an ArticleSection
     */
    static isArticleSection(message) {
        return message?.article_info !== undefined;
    }
    static mergePlaceBlocks(placeBlocks) {
        const actualPlaceBlocks = placeBlocks.filter((block) => !!block);
        if (!actualPlaceBlocks.length) {
            return undefined;
        }
        // any updates are only at the top-level review_summary
        const [firstPlace, ...restPlaces] = actualPlaceBlocks;
        const mergedPlace = Object.assign({}, firstPlace, ...restPlaces.map((place) => ({
            review_summary: place.review_summary,
        })));
        return mergedPlace;
    }
    static mergeMediaBlocks(mediaBlocks) {
        const actualMediaBlocks = mediaBlocks.filter((block) => !!block);
        if (!actualMediaBlocks.length) {
            return undefined;
        }
        // Combine all media items from all blocks
        const mergedMediaBlock = {
            media_items: actualMediaBlocks.flatMap((block) => block?.media_items || []),
            // for generated images, only take from the most recent block
            generated_media_items: actualMediaBlocks[actualMediaBlocks.length - 1].generated_media_items || [],
            // Use the progress from the latest block
            progress: actualMediaBlocks[actualMediaBlocks.length - 1].progress,
        };
        return mergedMediaBlock;
    }
    static mergeShoppingBlocks(shoppingBlocks) {
        const actualShoppingBlocks = shoppingBlocks.filter((block) => !!block);
        if (!actualShoppingBlocks.length) {
            return undefined;
        }
        // any updates are only at the top-level review_summary
        const [firstShoppingBlock, ...restShoppingBlocks] = actualShoppingBlocks;
        const mergedShoppingBlock = Object.assign({}, firstShoppingBlock, ...restShoppingBlocks.map((block) => ({
            review_summary: block.review_summary,
        })));
        return mergedShoppingBlock;
    }
    static mergeEntityListItems(entities) {
        const orderedItemsToMergeByIndex = groupBy(entities, (entity) => entity.index?.toString() ?? '0');
        const orderedMergedItems = [];
        Object.values(orderedItemsToMergeByIndex).forEach((entityAndUpdates) => {
            const firstUpdate = entityAndUpdates[0];
            orderedMergedItems.push({
                title: firstUpdate.title,
                text: entityAndUpdates.map(({ text }) => text).join(''),
                chunks: entityAndUpdates.flatMap((block) => block.chunks ?? []).filter(Boolean),
                chunk_starting_offset: 0,
                index: firstUpdate.index,
                awaiting_backfill: entityAndUpdates.reduce((latest, update) => update.awaiting_backfill ?? latest, false),
                place_block: MessageHelper.mergePlaceBlocks(entityAndUpdates.map((e) => e.place_block)),
                shopping_block: MessageHelper.mergeShoppingBlocks(entityAndUpdates.map((e) => e.shopping_block)),
            });
        });
        return orderedMergedItems;
    }
    static parseStructuredAnswerBlocks(message) {
        if (!this.isPerplexityMessage(message)) {
            return null;
        }
        if (!message.structured_answer_block_usages?.length) {
            return null;
        }
        const orderedBlocksByUsage = groupBy(message.blocks ?? [], (block) => block.intended_usage);
        const rejoinedBlocks = [];
        message.structured_answer_block_usages.forEach((usage) => {
            const blocks = orderedBlocksByUsage[usage];
            if (!blocks) {
                return;
            }
            const { markdownBlocks, placeBlocks, mediaBlocks, shoppingPreviewBlocks, entityListBlocks, jobsPreviewBlocks, placesPreviewBlocks, knowledgeCardsBlocks, calendarEventBlocks, entityGroupBlocks, entityGroupV2Blocks, assetsPreviewBlocks, assetsInlineBlocks, shoppingBlocks, placeholderBlocks, multipleChoiceQuizCardBlocks, flashCardSectionBlocks, jsonBlocks, } = blocks.reduce((acc, curr) => {
                if (curr.markdown_block) {
                    acc.markdownBlocks.push(curr.markdown_block);
                }
                else if (curr.inline_entity_block?.place_block) {
                    acc.placeBlocks.push(curr.inline_entity_block.place_block);
                }
                else if (curr.inline_entity_block?.media_block) {
                    acc.mediaBlocks.push(curr.inline_entity_block.media_block);
                }
                else if (curr.inline_entity_block?.shopping_preview_block) {
                    acc.shoppingPreviewBlocks.push(curr.inline_entity_block.shopping_preview_block);
                }
                else if (curr.entity_list_block) {
                    acc.entityListBlocks.push(curr.entity_list_block);
                }
                else if (curr.inline_entity_block?.jobs_preview_block) {
                    acc.jobsPreviewBlocks.push(curr.inline_entity_block.jobs_preview_block);
                }
                else if (curr.inline_entity_block?.places_preview_block) {
                    acc.placesPreviewBlocks.push(curr.inline_entity_block.places_preview_block);
                }
                else if (curr.inline_entity_block?.knowledge_card_block) {
                    acc.knowledgeCardsBlocks.push(curr.inline_entity_block.knowledge_card_block);
                }
                else if (curr.inline_entity_block?.calendar_event_block) {
                    acc.calendarEventBlocks.push(curr.inline_entity_block.calendar_event_block);
                }
                else if (curr.entity_group_block) {
                    acc.entityGroupBlocks.push(curr.entity_group_block);
                }
                else if (curr.entity_group_v2_block) {
                    acc.entityGroupV2Blocks.push(curr.entity_group_v2_block);
                }
                else if (curr.inline_entity_block?.assets_preview_block) {
                    acc.assetsPreviewBlocks.push(curr.inline_entity_block.assets_preview_block);
                }
                else if (curr.inline_entity_block?.assets_inline_block) {
                    acc.assetsInlineBlocks.push(curr.inline_entity_block.assets_inline_block);
                }
                else if (curr.inline_entity_block?.shopping_block) {
                    acc.shoppingBlocks.push(curr.inline_entity_block?.shopping_block);
                }
                else if (curr.inline_entity_block?.placeholder_block) {
                    acc.placeholderBlocks.push(curr.inline_entity_block?.placeholder_block);
                }
                else if (curr.inline_entity_block?.multiple_choice_quiz_card_block) {
                    acc.multipleChoiceQuizCardBlocks.push(curr.inline_entity_block.multiple_choice_quiz_card_block);
                }
                else if (curr.inline_entity_block?.flash_card_section_block) {
                    acc.flashCardSectionBlocks.push(curr.inline_entity_block.flash_card_section_block);
                }
                else if (curr.json_block) {
                    acc.jsonBlocks.push(curr.json_block);
                }
                return acc;
            }, {
                markdownBlocks: [],
                placeBlocks: [],
                mediaBlocks: [],
                shoppingPreviewBlocks: [],
                entityListBlocks: [],
                jobsPreviewBlocks: [],
                placesPreviewBlocks: [],
                knowledgeCardsBlocks: [],
                calendarEventBlocks: [],
                entityGroupBlocks: [],
                entityGroupV2Blocks: [],
                assetsPreviewBlocks: [],
                assetsInlineBlocks: [],
                shoppingBlocks: [],
                placeholderBlocks: [],
                multipleChoiceQuizCardBlocks: [],
                flashCardSectionBlocks: [],
                jsonBlocks: [],
            });
            if (markdownBlocks.length) {
                const latestMarkdownBlock = markdownBlocks[markdownBlocks.length - 1];
                const chunks = markdownBlocks
                    .flatMap((block) => block.chunks ?? [])
                    .filter(Boolean);
                rejoinedBlocks.push({
                    intended_usage: usage,
                    markdown_block: {
                        answer: chunks.join(''),
                        chunks,
                        chunk_starting_offset: 0,
                        progress: latestMarkdownBlock.progress,
                        media_items: markdownBlocks.flatMap((block) => block.media_items || []),
                        inline_token_annotations: markdownBlocks.flatMap((block) => block.inline_token_annotations || []),
                    },
                });
            }
            if (shoppingPreviewBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        shopping_preview_block: shoppingPreviewBlocks[shoppingPreviewBlocks.length - 1],
                    },
                });
            }
            if (jobsPreviewBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        jobs_preview_block: jobsPreviewBlocks[jobsPreviewBlocks.length - 1],
                    },
                });
            }
            if (placesPreviewBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        places_preview_block: placesPreviewBlocks[placesPreviewBlocks.length - 1],
                    },
                });
            }
            if (placeBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        place_block: MessageHelper.mergePlaceBlocks(placeBlocks),
                    },
                });
            }
            if (mediaBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        media_block: MessageHelper.mergeMediaBlocks(mediaBlocks),
                    },
                });
            }
            if (entityListBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    entity_list_block: {
                        text: entityListBlocks.map(({ text }) => text).join(''),
                        chunks: entityListBlocks
                            .flatMap((block) => block.chunks ?? [])
                            .filter(Boolean),
                        chunk_starting_offset: 0,
                        entities: MessageHelper.mergeEntityListItems(entityListBlocks
                            .flatMap((block) => block.entities ?? [])
                            .filter(Boolean)),
                    },
                });
            }
            if (knowledgeCardsBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        knowledge_card_block: knowledgeCardsBlocks[knowledgeCardsBlocks.length - 1],
                    },
                });
            }
            if (calendarEventBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        calendar_event_block: calendarEventBlocks[calendarEventBlocks.length - 1],
                    },
                });
            }
            if (entityGroupBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    entity_group_block: {
                        entities: entityGroupBlocks[entityGroupBlocks.length - 1].entities,
                        entity_group_type: entityGroupBlocks[entityGroupBlocks.length - 1].entity_group_type,
                    },
                });
            }
            if (entityGroupV2Blocks.length) {
                const latestEntityGroupV2Block = entityGroupV2Blocks[entityGroupV2Blocks.length - 1];
                rejoinedBlocks.push({
                    intended_usage: usage,
                    entity_group_v2_block: latestEntityGroupV2Block,
                });
            }
            if (assetsPreviewBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        assets_preview_block: assetsPreviewBlocks[assetsPreviewBlocks.length - 1],
                    },
                });
            }
            if (assetsInlineBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        assets_inline_block: assetsInlineBlocks[assetsInlineBlocks.length - 1],
                    },
                });
            }
            if (shoppingBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        shopping_block: this.mergeShoppingBlocks(shoppingBlocks),
                    },
                });
            }
            if (placeholderBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        placeholder_block: placeholderBlocks[placeholderBlocks.length - 1],
                    },
                });
            }
            if (multipleChoiceQuizCardBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        multiple_choice_quiz_card_block: multipleChoiceQuizCardBlocks[multipleChoiceQuizCardBlocks.length - 1],
                    },
                });
            }
            if (flashCardSectionBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    inline_entity_block: {
                        flash_card_section_block: flashCardSectionBlocks[flashCardSectionBlocks.length - 1],
                    },
                });
            }
            if (jsonBlocks.length) {
                rejoinedBlocks.push({
                    intended_usage: usage,
                    json_block: jsonBlocks[jsonBlocks.length - 1],
                });
            }
        });
        return rejoinedBlocks;
    }
    /**
     * Parse the text field from a message, handling both legacy and new formats
     */
    static parseAskTextField(message) {
        if (!this.isPerplexityMessage(message)) {
            return this.legacyParseBackendOutputStr(message.text);
        }
        if (!message.blocks?.length)
            return null;
        const parsed = { answer: '' };
        // assemble structured answer blocks if returned
        parsed.structured_answer_blocks = MessageHelper.parseStructuredAnswerBlocks(message);
        // find all markdown blocks with intended_usage "ask_text" and combine their content
        const markdownBlocks = message.blocks
            ?.filter((block) => block.intended_usage === 'ask_text' && block.markdown_block)
            .map((block) => block.markdown_block);
        if (markdownBlocks.length > 0) {
            // join all chunks to form the complete answer
            const chunks = markdownBlocks
                .flatMap((block) => block.chunks ?? [])
                .filter(Boolean);
            parsed.chunks = chunks;
            parsed.answer = chunks.join('');
        }
        // find and merge all web result blocks with intended_usage "web_results"
        const webResults = message.blocks
            ?.filter((block) => block.intended_usage === 'web_results' && block.web_result_block)
            .flatMap((block) => block.web_result_block.web_results ?? [])
            .filter(Boolean);
        if (webResults.length > 0) {
            parsed.web_results = webResults;
        }
        return parsed;
    }
    /**
     * Parse backend output string into MessageTextParsed format
     */
    static legacyParseBackendOutputStr(outputStr) {
        let parsed = null;
        try {
            parsed = JSON.parse(outputStr ?? '');
            if (Array.isArray(parsed)) {
                const lastEl = parsed[parsed.length - 1];
                if (lastEl.step_type == 'FINAL' && lastEl?.content?.answer != null) {
                    parsed = JSON.parse(lastEl.content.answer);
                }
            }
        }
        catch {
            return null;
        }
        return parsed;
    }
    /**
     * Check if message status is pending
     */
    static isStatusPending(message) {
        return message.status?.toLowerCase() === 'pending';
    }
    /**
     * Check if message status is blocked
     */
    static isStatusBlocked(message) {
        return message.status?.toLowerCase() === 'blocked';
    }
    /**
     * Check if message status is failed
     */
    static isStatusFailed(message) {
        return message.status?.toLowerCase() === 'failed';
    }
    /**
     * Check if message status is completed
     */
    static isStatusCompleted(message) {
        return message.status?.toLowerCase() === 'completed';
    }
    /**
     * True when the entry has reached a terminal status (completed / blocked /
     * failed) AND the backend has signaled the stream is final
     * (`reconnectable` is false). `reconnectable` stays true through the whole
     * stream lifecycle — including transient COMPLETED flickers with partial
     * content during agent post-processing — so combining it with a terminal
     * status gives the canonical "the user has seen the final answer state"
     * signal. Used to gate both `markThreadRead` (in `useThreadEffects`) and
     * the `computer answer viewed` analytics event.
     */
    static shouldMarkThreadView(message) {
        if (message.reconnectable)
            return false;
        return (MessageHelper.isStatusCompleted(message) ||
            MessageHelper.isStatusBlocked(message) ||
            MessageHelper.isStatusFailed(message));
    }
    /**
     * Check if message is in copilot mode
     */
    static isCopilotMode(message) {
        return message.mode?.toLowerCase() === 'copilot';
    }
    /**
     * Check if message is in article mode
     */
    static isArticleMode(message) {
        if (this.isPerplexityMessage(message)) {
            return message?.mode === 'ARTICLE';
        }
        if (this.isPerplexityArticleSection(message)) {
            return message?.message?.mode === 'ARTICLE';
        }
        // @ts-expect-error -- here we have some legacy shenanigans with Article being still of an old type legacy message type and format.
        return message?.mode === 'article';
    }
    /**
     * Type guard for source list type
     */
    static isSourceListType(sources) {
        return (Array.isArray(sources) &&
            sources.every((s) => Object.values(Source).includes(s)));
    }
}
//# sourceMappingURL=MessageHelper.js.map